import React, { useEffect, useState, Component } from 'react';
import { X, User, Building2, Users, CheckCircle2, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from './ui/Button';
const SUPPORT_EMAIL = 'support@zanoplatform.com';
// Web3Forms delivers submissions straight to your inbox — no email client.
// Get a free access key at https://web3forms.com and set its destination to
// support@zanoplatform.com, then paste the key below.
const WEB3FORMS_ACCESS_KEY = 'daa0e880-90c5-42b7-911c-ac1a7e0e4820';
export const AgencyModal = ({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referred, setReferred] = useState('');
  // Reset back to the form whenever the modal is fully closed.
  useEffect(() => {
    if (!isOpen) {
      const t = window.setTimeout(() => {
        setSubmitted(false);
        setError(null);
        setSubmitting(false);
        setReferred('');
      }, 250);
      return () => window.clearTimeout(t);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-zano-pink focus:border-zano-pink outline-none text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const rainConfetti = () => {
    const duration = 2500;
    const end = Date.now() + duration;
    const colors = ['#EC4899', '#F9A8D4', '#C4B5FD', '#38BDF8', '#FBCFE8'];
    // Initial celebratory burst
    confetti({
      particleCount: 120,
      spread: 90,
      origin: {
        y: 0.3
      },
      colors
    });
    // Continuous rain from the top
    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 90,
        spread: 70,
        startVelocity: 35,
        origin: {
          x: Math.random(),
          y: -0.1
        },
        gravity: 0.8,
        colors
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const data = new FormData(e.currentTarget);
    const get = (k: string) => data.get(k) as string || '—';
    // Send the submission straight to support@zanoplatform.com via Web3Forms.
    const message = ['ABOUT YOU', `Full name: ${get('fullName')}`, `Role / title: ${get('role')}`, `Email: ${get('email')}`, `LinkedIn profile URL: ${get('linkedin')}`, '', 'YOUR AGENCY', `Agency name: ${get('agencyName')}`, `Website: ${get('website')}`, `Number of creators: ${get('creators')}`, '', 'YOUR CREATORS', `Roster link: ${get('rosterLink')}`, `Primary niche: ${get('niche')}`, `Notes: ${get('notes')}`, '', 'REFERRAL', `Referred by an existing Zano user?: ${get('referred')}`, `Referred by: ${get('referredBy')}`].join('\n');
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New agency listing request',
      from_name: 'Zano — List Your Agency form',
      replyto: get('email'),
      message
    };
    try {
      setSubmitting(true);
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || 'Submission failed');
      }
      rainConfetti();
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong sending your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };
  return <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-29" data-mp="i8w88nxo5zqm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden relative max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-id="element-30" data-mp="db4fxst44blw">
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10" data-id="element-31" data-mp="ma7xutgjmkyt">
          <X size={20} data-id="element-32" data-mp="w8oua5y93rs9" />
        </button>

        {submitted ? <div className="p-8 sm:p-10 text-center" data-id="element-33" data-mp="w8f4aid7hkmk">
            <div className="mx-auto mb-5 flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50" data-id="element-34" data-mp="1a0lm08w0bzo4">
              <CheckCircle2 size={32} className="text-emerald-500" data-id="element-35" data-mp="15zjj9i2vt5he" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-36" data-mp="1jxgh0ht2nhxn">
              Request received!
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-37" data-mp="1f07zmjfqzhxg">
              Our team will review your request and respond within 48 hours. If
              approved, you'll receive an invite to the marketplace to create
              your account for your agency — and you'll be able to invite the
              rest of your team.
            </p>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6" data-id="element-38" data-mp="1tlvg9umqs4bk">
              <Mail size={14} className="text-zano-pink" data-id="element-39" data-mp="1fugyfr4km963" />
              <span data-id="element-40" data-mp="1fsw524tzsw9t">
                Sent to{' '}
                <span className="font-medium text-zano-charcoal" data-id="element-41" data-mp="1kkxdtgryc4v3">
                  {SUPPORT_EMAIL}
                </span>
              </span>
            </div>
            <Button variant="primary" className="w-full justify-center" onClick={onClose} data-id="element-42" data-mp="15y45wwkri1cx">
              Done
            </Button>
          </div> : <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-43" data-mp="wxyzaone76zt">
            <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-44" data-mp="10w54hs8l1eim">
              List Your Agency
            </h3>
            <p className="text-gray-600 mb-6 text-sm" data-id="element-45" data-mp="33lg1zj8n67f4">
              Join 400+ talent agencies on Zano. Tell us a little about you,
              your agency, and your creators to get started.
            </p>
            <form className="space-y-6" onSubmit={handleSubmit} data-id="element-46" data-mp="2tubo6mupcslv">
              {/* About you */}
              <div data-id="element-47" data-mp="2owsapp5twjuz">
                <div className="flex items-center gap-2 mb-3" data-id="element-48" data-mp="2ot4hys87k1hb">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-49" data-mp="3mi7ixm0poag0">
                    <User size={14} data-id="element-50" data-mp="3hko86w382fst" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-51" data-mp="3ckjmmrngiuvq">
                    About you
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-52" data-mp="38i6ho5gjb100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-53" data-mp="38laxk03d3l6i">
                    <div data-id="element-54" data-mp="216povqsica4t">
                      <label className={labelClass} data-id="element-55" data-mp="1x3syhdwq79qb">Full name</label>
                      <input name="fullName" type="text" required className={inputClass} data-id="element-56" data-mp="1x2hubczdbzyo" />
                    </div>
                    <div data-id="element-57" data-mp="lmf5u6su4nc6">
                      <label className={labelClass} data-id="element-58" data-mp="lm6syewppi0z">Role / title</label>
                      <input name="role" type="text" placeholder="e.g. Talent Manager" className={inputClass} data-id="element-59" data-mp="qd5g9yg4bgdq" />
                    </div>
                  </div>
                  <div data-id="element-60" data-mp="10dfp76v4e1n3">
                    <label className={labelClass} data-id="element-61" data-mp="14d6fl9gqifr3">Email</label>
                    <input name="email" type="email" required className={inputClass} data-id="element-62" data-mp="14dq08xxrri8t" />
                  </div>
                  <div data-id="element-63" data-mp="366nvt1ixkoxg">
                    <label className={labelClass} data-id="element-64" data-mp="3b2uq1x1ysbzk">LinkedIn profile URL</label>
                    <input name="linkedin" type="url" placeholder="https://linkedin.com/in/you" className={inputClass} data-id="element-65" data-mp="1gviv6qgyiqpp" />
                  </div>
                </div>
              </div>

              {/* Your agency */}
              <div data-id="element-66" data-mp="1llz7g791k6ah">
                <div className="flex items-center gap-2 mb-3" data-id="element-67" data-mp="1lnk36073dv9x">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-68" data-mp="xweldfhlcswz">
                    <Building2 size={14} data-id="element-69" data-mp="tv2ul6mze1n7" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-70" data-mp="twns954le0pa">
                    Your agency
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-71" data-mp="12w0zrs95of7d">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-72" data-mp="12u6eudxvwn3a">
                    <div data-id="element-73" data-mp="2j97wgc893xh8">
                      <label className={labelClass} data-id="element-74" data-mp="2j8g3l961gdun">Agency name</label>
                      <input name="agencyName" type="text" required className={inputClass} data-id="element-75" data-mp="2ebpr0vtg00dz" />
                    </div>
                    <div data-id="element-76" data-mp="53jkqgke0u0t">
                      <label className={labelClass} data-id="element-77" data-mp="3n7uw6w0bhidl">Website</label>
                      <input name="website" type="text" placeholder="agency.com" className={inputClass} data-id="element-78" data-mp="3n9ph4abl9aho" />
                    </div>
                  </div>
                  <div data-id="element-79" data-mp="nroin7ed5krp">
                    <label className={labelClass} data-id="element-80" data-mp="nry8muzerrxe">Number of creators</label>
                    <select name="creators" className={inputClass} data-id="element-81" data-mp="a0a0f2sa5nlp">
                      <option data-id="element-82" data-mp="f063mzt5lb07">1-10</option>
                      <option data-id="element-83" data-mp="eyuyownz1x7i">11-50</option>
                      <option data-id="element-84" data-mp="12q0excxtqric">51-100</option>
                      <option data-id="element-85" data-mp="12p8i5yshqnqh">100+</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Your creators */}
              <div data-id="element-86" data-mp="2q3evvhzb23ec">
                <div className="flex items-center gap-2 mb-3" data-id="element-87" data-mp="2q4yc58kyoeg4">
                  <div className="p-1.5 rounded-md bg-zano-blush text-zano-pink" data-id="element-88" data-mp="2v2rgdw2peydr">
                    <Users size={14} data-id="element-89" data-mp="2v0nd9gcmq3ej" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900" data-id="element-90" data-mp="2h2gr2xh1lrup">
                    Your creators
                  </h4>
                </div>
                <div className="space-y-4" data-id="element-91" data-mp="27df6etzjymae">
                  <div data-id="element-92" data-mp="27cnebt55z6ov">
                    <label className={labelClass} data-id="element-93" data-mp="27bc9dpzzfsw6">
                      Roster link{' '}
                      <span className="text-gray-400 font-normal" data-id="element-94" data-mp="2c5ggqad5u2xs">
                        (optional)
                      </span>
                    </label>
                    <input name="rosterLink" type="text" placeholder="Link to your roster or media kit" className={inputClass} data-id="element-95" data-mp="22dkj10ezkeu3" />
                  </div>
                  <div data-id="element-96" data-mp="1tfgxi4403i2a">
                    <label className={labelClass} data-id="element-97" data-mp="19rm80c7h3vq2">Primary niche</label>
                    <select name="niche" defaultValue="" className={inputClass} data-id="element-98" data-mp="19tp51g71mp4a">
                      <option value="" disabled data-id="element-99" data-mp="14ulytsfbucuq">
                        Select a niche
                      </option>
                      <option data-id="element-100" data-mp="1iq7kpqq9kgix">Fashion</option>
                      <option data-id="element-101" data-mp="1epw0ug5q6k8i">Beauty</option>
                      <option data-id="element-102" data-mp="1et1rhzu05u6c">Lifestyle</option>
                      <option data-id="element-103" data-mp="1spye1p338phf">Tech</option>
                      <option data-id="element-104" data-mp="1sovmrg1usl0z">Gaming</option>
                      <option data-id="element-105" data-mp="1ns5cj9cqcjnh">Fitness</option>
                      <option data-id="element-106" data-mp="1nrdelrvnu9u1">Food</option>
                      <option data-id="element-107" data-mp="22g9uuc7vet5q">Travel</option>
                      <option data-id="element-108" data-mp="22g04uomtsm01">Mixed / Multiple</option>
                    </select>
                  </div>
                  <div data-id="element-109" data-mp="1xhyj1vy0m8kv">
                    <label className={labelClass} data-id="element-110" data-mp="1xjswyqavd0fg">
                      Anything else?{' '}
                      <span className="text-gray-400 font-normal" data-id="element-111" data-mp="2beva9yi1ps2a">
                        (optional)
                      </span>
                    </label>
                    <textarea name="notes" rows={2} placeholder="Tell us more about your roster" className={inputClass} data-id="element-112" data-mp="145ry37y2s4tj" />
                  </div>
                </div>
              </div>

              {/* Referral */}
              <div data-id="element-113" data-mp="1hxetl5k719b9">
                <div className="space-y-4" data-id="element-114" data-mp="1hxydgrtem7rx">
                  <div data-id="element-115" data-mp="1mu57pncftuu1">
                    <label className={labelClass} data-id="element-116" data-mp="1muot5e1ar1ct">
                      Were you referred by an existing Zano user?
                    </label>
                    <select name="referred" value={referred} onChange={e => setReferred(e.target.value)} className={inputClass} data-id="element-117" data-mp="1wtnppy8sf6jg">
                      <option value="" disabled data-id="element-118" data-mp="2gocgbrvqofc">
                        Select an option
                      </option>
                      <option data-id="element-119" data-mp="6h88td4mpo0f">No</option>
                      <option data-id="element-120" data-mp="3v3d2erltgg6g">Yes</option>
                    </select>
                  </div>
                  {referred === 'Yes' && <div data-id="element-121" data-mp="3qajy24u9f9uw">
                      <label className={labelClass} data-id="element-122" data-mp="3qa0i2to66vji">Who referred you?</label>
                      <input name="referredBy" type="text" placeholder="Name of the Zano user who referred you" className={inputClass} data-id="element-123" data-mp="3ldjp9m60zt03" />
                    </div>}
                </div>
              </div>

              <div className="flex items-start gap-3" data-id="element-124" data-mp="8vli8hgjm10m">
                <input type="checkbox" id="tos" name="tos" required className="mt-1 shrink-0 rounded border-gray-300 text-zano-pink focus:ring-zano-pink" data-id="element-125" data-mp="8u23wweg601h" />
                <label htmlFor="tos" className="text-[11px] text-gray-500 leading-relaxed" data-id="element-126" data-mp="2om82k3yw6yzl">
                  By checking this box, I acknowledge and agree to the Terms of
                  Service and Privacy Policy by Zano Platform. I acknowledge
                  that Zano Platform may collect, process, and store my personal
                  data provided in this form for the purpose of evaluating my
                  request and providing access to the platform. I understand
                  that my data will be handled in accordance with the Zano
                  Privacy Policy and GDPR regulations.
                </label>
              </div>

              {error && <p className="text-sm text-red-600" role="alert" data-id="element-127" data-mp="q3cufpk6d0pm">
                  {error}
                </p>}
              <Button variant="primary" className="w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" type="submit" disabled={submitting} data-id="element-128" data-mp="v4sdxd6pez5w">
                {submitting ? 'Sending…' : 'Submit'}
              </Button>
            </form>
          </div>}
      </div>
    </div>;
};